import { createContext, useContext } from 'react';
import { listLookup, LookupRecord } from '../../../api/masterData';
import { createQualification, CreateQualificationPayload, deleteQualification, listQualifications } from '../../../api/academic';
import { getStudent, listStudentDocuments, uploadStudentDocument } from '../../../api/students';
import { getStudentSignature, getStudentSignatureImage, saveStudentSignature, SignatureMethod, StudentSignatureMeta } from '../../../api/studentSignature';
import { Qualification } from '../../../types/academic';
import { StudentListItem } from '../../../types/student';

/** Where the University Application Form's steps read and write their data
 * (2026-11-06). The counsellor's wizard uses the staff API (the default);
 * the Student Portal's Application Form provides the student's own,
 * student-scoped endpoints — so both use the very same steps, fields and
 * validation. */
export interface ApplicationFormApi {
  /** 'staff' — the counsellor's wizard; 'portal' — the student's own form. */
  mode: 'staff' | 'portal';
  listLookup: (path: string, params?: Record<string, unknown>) => Promise<LookupRecord[]>;
  listQualifications: (studentId: number) => Promise<Qualification[]>;
  createQualification: (studentId: number, payload: CreateQualificationPayload) => Promise<Qualification>;
  deleteQualification: (qualificationId: number) => Promise<void>;
  /** Only what the photo upload on Step 1 needs. */
  listDocuments: (studentId: number) => Promise<Array<{ id: number; originalName: string; documentType: { name: string } }>>;
  uploadDocument: (studentId: number, file: File, documentTypeId: number) => Promise<unknown>;
  getSignature: (studentId: number) => Promise<StudentSignatureMeta | null>;
  getSignatureImage: (studentId: number) => Promise<{ blob: Blob; mimeType: string }>;
  saveSignature: (studentId: number, method: SignatureMethod, file: Blob) => Promise<StudentSignatureMeta>;
  getStudent: (studentId: number) => Promise<StudentListItem>;
}

export const staffApplicationFormApi: ApplicationFormApi = {
  mode: 'staff',
  listLookup,
  listQualifications,
  createQualification,
  deleteQualification,
  listDocuments: (studentId) => listStudentDocuments(studentId),
  uploadDocument: (studentId, file, documentTypeId) => uploadStudentDocument(studentId, file, documentTypeId),
  getSignature: getStudentSignature,
  getSignatureImage: getStudentSignatureImage,
  saveSignature: saveStudentSignature,
  getStudent,
};

export const ApplicationFormApiContext = createContext<ApplicationFormApi>(staffApplicationFormApi);
export const useApplicationFormApi = () => useContext(ApplicationFormApiContext);
